<template>
    <div class="marketing-card marketing-analytics mt-4 mb-4" ref="analyticsCard">
        <!-- Header Section -->
        <div class="marketing-header">
            <div class="header-left">
                <h4 class="header-title">
                    Marketing Analytics
                </h4>
                <p class="header-subtitle">Comprehensive marketings performance overview</p>
            </div>
            <div class="header-actions">
                <button class="btn btn-analytic-action btn-analytic-secondary me-2" @click="closeAnalytics">
                    <i class="fas fa-arrow-up me-1"></i>
                    <span class="ms-1">Close Analytics</span>
                </button>
            </div>
        </div>

        <!-- Content Section -->
        <div class="marketing-content">
            <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border primary-text" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted mt-2">Loading analytics data...</p>
            </div>

            <template v-else>
            <div class="row mb-4 g-3">
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="d-flex align-items-center mb-2">
                            <div class="icon-box bg-light-primary primary-text me-2">
                                <i class="fas fa-bullhorn"></i>
                            </div>
                        </div>
                        <p class="text-muted mb-1 small">Total Marketings</p>
                        <h3 class="fw-bold mb-0">{{ formatNumber(data.total_marketings) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="d-flex align-items-center mb-2">
                            <div class="icon-box bg-light-success text-success me-2">
                                <i class="fas fa-calendar-alt"></i>
                            </div>
                        </div>
                        <p class="text-muted mb-1 small">Total Campaigns</p>
                        <h3 class="fw-bold mb-0">{{ formatNumber(data.total_campaigns) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="d-flex align-items-center mb-2">
                            <div class="icon-box bg-light-danger text-danger me-2">
                                <i class="fas fa-calendar-check"></i>
                            </div>
                        </div>
                        <p class="text-muted mb-1 small">Total Events</p>
                        <h3 class="fw-bold mb-0">{{ formatNumber(data.total_events) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="summary-card">
                        <div class="d-flex align-items-center mb-2">
                            <div class="icon-box bg-light-warning text-warning me-2">
                                <i class="fas fa-users"></i>
                            </div>
                        </div>
                        <p class="text-muted mb-1 small">Total Members Registered</p>
                        <h3 class="fw-bold mb-0">{{ formatNumber(data.total_members) }}</h3>
                    </div>
                </div>
            </div>

            <div class="row g-3">
                <div class="col-md-8">
                    <div class="chart-card card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap">
                                <div>
                                    <h5 class="fw-bold mb-1">Activity Overview</h5>
                                    <p class="text-muted mb-0 small">Events and Members over time</p>
                                </div>
                                <div class="btn-group mt-2 mt-md-0" role="group">
                                    <button type="button" class="btn btn-sm" :class="timeScale === 'day' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setTimeScale('day')">Daily</button>
                                    <button type="button" class="btn btn-sm" :class="timeScale === 'week' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setTimeScale('week')">Weekly</button>
                                    <button type="button" class="btn btn-sm" :class="timeScale === 'month' ? 'btn-secondary' : 'btn-outline-secondary'" @click="setTimeScale('month')">Monthly</button>
                                </div>
                            </div>
                            <apexchart type="area" height="300" :options="areaChartOptions" :series="areaChartSeries"></apexchart>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="chart-card card h-100 border-0 shadow-sm">
                        <div class="card-body">
                            <h5 class="fw-bold mb-1">Members by Campaign</h5>
                            <p class="text-muted mb-4 small">Distribution of members across active campaigns</p>
                            
                            <div v-if="data.members_by_campaign.length > 0" class="d-flex justify-content-center">
                                <apexchart type="donut" width="100%" height="300" :options="donutChartOptions" :series="donutChartSeries"></apexchart>
                            </div>
                            <div v-else class="text-center text-muted mt-5 pb-5">
                                <i class="fas fa-chart-pie fa-3x mb-3 text-light"></i>
                                <p>No member data found across campaigns.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </template>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import api from '@/helpers/api'

export default {
    name: 'MarketingAnalytics',
    components: {
        apexchart: VueApexCharts,
    },
    emits: ['close'],
    setup(props, { emit }) {
        const analyticsCard = ref(null)
        const isLoading = ref(false)
        const timeScale = ref('day')
        
        const data = ref({
            total_marketings: 0,
            total_campaigns: 0,
            total_events: 0,
            total_members: 0,
            activity_overview: [],
            members_by_campaign: [],
        })

        const formatNumber = (value) => {
            const intValue = parseInt(value, 10) || 0;
            return intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        };

        const loadAnalytics = async () => {
            isLoading.value = true
            try {
                const filterParams = {}
                if (timeScale.value) {
                    filterParams.time_scale = timeScale.value
                }

                const result = await api.request('get', '/api/marketing/analytics', { 
                    filter: filterParams
                })
                if (result.data?.data) {
                    data.value = result.data.data
                }
            } catch (err) {
                console.error("Failed to load marketing analytics", err)
            } finally {
                isLoading.value = false
            }
        }

        const setTimeScale = async (scale) => {
            timeScale.value = scale
            await loadAnalytics()
            await nextTick()
            if (analyticsCard.value) {
                analyticsCard.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }

        onMounted(() => {
            loadAnalytics()
        })

        const areaChartOptions = computed(() => {
            const categories = data.value.activity_overview.map(item => item.label || item.date)
            return {
                chart: {
                    type: 'area',
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                colors: ['#20c997', '#845ef7'],
                dataLabels: { enabled: false },
                stroke: { curve: 'smooth', width: 2 },
                xaxis: {
                    categories: categories,
                    labels: {
                        formatter: function(val) {
                            return val // The backend now provides exactly what to show (tgl 1, week 1, jan)
                        }
                    }
                },
                yaxis: {
                    labels: {
                        formatter: (value) => { return Math.round(value) }
                    }
                },
                legend: {
                    position: 'bottom',
                    horizontalAlign: 'center',
                    markers: {
                        radius: 12
                    }
                },
                fill: {
                    type: ['solid', 'solid'],
                    opacity: [0.1, 0]
                }
            }
        })

        const areaChartSeries = computed(() => {
            return [
                {
                    name: 'Total Events',
                    data: data.value.activity_overview.map(item => item.total_events)
                },
                {
                    name: 'Total Members',
                    data: data.value.activity_overview.map(item => item.total_members)
                }
            ]
        })

        const donutChartOptions = computed(() => {
            const labels = data.value.members_by_campaign.map(item => item.name)
            return {
                chart: {
                    type: 'donut',
                },
                labels: labels,
                colors: ['#ff6b6b', '#20c997', '#4dabf7', '#fcc419', '#845ef7'],
                plotOptions: {
                    pie: {
                        donut: {
                            size: '75%',
                            labels: {
                                show: true,
                                name: { 
                                    show: true,
                                    fontSize: '14px',
                                    color: '#6b7280'
                                },
                                value: { 
                                    show: true,
                                    fontSize: '22px',
                                    fontWeight: 'bold',
                                    color: '#111827'
                                },
                                total: {
                                    show: true,
                                    label: 'Total Members',
                                    fontSize: '14px',
                                    color: '#6b7280',
                                    formatter: function (w) {
                                        return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
                                    }
                                }
                            }
                        }
                    }
                },
                dataLabels: {
                    enabled: false
                },
                legend: {
                    position: 'bottom',
                    markers: {
                        radius: 12
                    }
                }
            }
        })

        const donutChartSeries = computed(() => {
            return data.value.members_by_campaign.map(item => parseInt(item.total_members))
        })

        const closeAnalytics = () => {
            emit('close')
        }

        return {
            analyticsCard,
            isLoading,
            timeScale,
            data,
            formatNumber,
            setTimeScale,
            areaChartOptions,
            areaChartSeries,
            donutChartOptions,
            donutChartSeries,
            closeAnalytics
        }
    }
}
</script>

<style scoped>
/* Main Card Pattern */
.marketing-card {
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    scroll-margin-top: 10px;
}
.marketing-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;
}
.header-left {
    flex: 1;
}
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}
.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}
.header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
}

.btn-analytic-action {
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    border-radius: 8px;
    transition: all 0.2s;
}

.btn-analytic-primary {
    background-color: var(--primary-color, #FC6666);
    color: white;
}

.btn-analytic-primary:hover {
    filter: brightness(0.9);
    color: white;
}

.btn-analytic-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.btn-analytic-secondary {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
}

.btn-analytic-secondary:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.marketing-content {
    background-color: #f7f9fc;
    padding: 1.5rem 2rem;
}

.title-text {
    color: #1e293b;
}
.summary-card {
    background: #fff;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.03);
    border: 1px solid #f0f0f0;
    transition: transform 0.2s ease-in-out;
}
.summary-card:hover {
    transform: translateY(-2px);
}
.icon-box {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
}
.bg-light-danger { background-color: #ffe3e3; }
.bg-light-success { background-color: #d3f9d8; }
.bg-light-primary { background-color: var(--secondary-color, #fee0e2); }
.bg-light-warning { background-color: #fff3bf; }

.chart-card {
    border-radius: 12px;
    background-color: #fff;
}

.btn-group {
    border-radius: 8px;
    border: 1px solid var(--secondary-color, #f7e7e8);
    overflow: hidden;
    background: #fff;
    padding: 0;
}

.btn-group .btn {
    border: none;
    border-right: 1px solid var(--secondary-color, #fee0e2);
    color: #64748b;
    background-color: transparent;
    transition: all 0.2s ease;
}

.btn-group .btn:last-child {
    border-right: none;
}

.btn-group .btn:hover {
    background-color: var(--secondary-color, #fee0e2);
    color: var(--primary-color, #F93E3E);
}

.btn-group .btn.btn-secondary {
    background-color: var(--primary-color, #F93E3E) !important;
    color: #fff !important;
    border-color: var(--primary-color, #F93E3E) !important;
}
</style>
